import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  ButtonGroup,
  useDisclosure,
  Alert,
  AlertTitle,
  AlertDescription,
  VStack,
  AlertIcon,
  Image,
  Link,
  useToast,
} from '@chakra-ui/react'
import * as React from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import Card from 'components/card/Card'
import { AiOutlineDelete, AiOutlinePlus } from 'react-icons/ai'
import { EditIcon } from '@chakra-ui/icons'
import { useDeleteSponsor, useGetSponsor } from 'queries/sponsors.query'
import { DeleteSponsorModal } from './DeleteSponsorModal'
import { CreateSponsorModal } from './CreateSponsorModal'
import { EditSponsorModal } from './EditSponsorModal'
import { CustomPagination } from 'views/admin/UserTable/componenets/pagination'
import { SkeletonTable } from 'components/skeleton/Skeleton'

const columnHelper = createColumnHelper()

export default function SponsorTable () {
  const [sorting, setSorting] = React.useState([])
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')
  const toast = useToast()
  const [isEditOpen, setIsEditOpen] = React.useState(false)

  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isCreateOpen,
    onOpen: onOpenCreate,
    onClose: onCloseCreate,
  } = useDisclosure()

  const [selectedSponsor, setSelectedSponsor] = React.useState(null)
  const [selectedSponsorForDeletion, setSelectedSponsorForDeletion] =
    React.useState(null)
  const { mutate: deleteSponsor, isPending } = useDeleteSponsor()

  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
    searchQuery: null,
  })

  const { data, isLoading, isError, refetch } = useGetSponsor(
    pagination.current,
    pagination.pageSize,
    pagination.searchQuery,
  )

  const rows = data?.data || []

  const onPageChange = page => {
    setPagination(prev => ({
      ...prev,
      current: page,
    }))
  }

  const onPageSizeChange = value => {
    setPagination(prev => ({
      ...prev,
      pageSize: value,
    }))
  }

  const handleEditOpen = sponsor => {
    setSelectedSponsor(sponsor)
    setIsEditOpen(true)
  }

  const handleEditClose = () => {
    setIsEditOpen(false)
    setSelectedSponsor(null)
  }

  const handleSingleDelete = id => {
    setSelectedSponsorForDeletion(id)
    onOpen()
  }

  const onDelete = () => {
    if (!selectedSponsorForDeletion) {
      onClose()
      return // Safety check
    } // Pass the ID directly to the mutation (e.g., deleteSponsor(5))

    deleteSponsor(selectedSponsorForDeletion, {
      onSuccess: () => {
        onClose()
        setSelectedSponsorForDeletion(null)
        refetch()
        toast({
          title: 'Sponsor Deleted.',
          description: 'The sponsor has been successfully deleted.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
      onError: err => {
        console.error('Delete failed:', err)
        toast({
          title: 'Failed to Delete Sponsor.',
          description:
            err.response?.data?.message ||
            'There was an error deleting the sponsor.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => (
        <Text
          justifyContent='center'
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Name
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm' fontWeight='700'>
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('website', {
      id: 'website',
      header: () => (
        <Text
          justifyContent='center'
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Website
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Link
            href={info.getValue()}
            isExternal
            color='blue.500'
            fontSize='sm'
          >
            {info.getValue()}
          </Link>
        </Flex>
      ),
    }),
    columnHelper.accessor('logo', {
      id: 'logo',
      header: () => (
        <Text
          justifyContent='center'
          align='center'
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
        >
          Logo
        </Text>
      ),
      cell: info => {
        const path = info.getValue()
        const baseUrl = process.env.REACT_APP_PUBLIC_BACKEND_URL || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : 'http://localhost:8000')
        
        let imageUrl = null;
        if (path) {
          if (path.startsWith('http')) {
            imageUrl = path;
          } else {
            // Remove leading slash if present, then ensure we don't duplicate /storage/
            const cleanPath = path.startsWith('/') ? path.substring(1) : path;
            const finalPath = cleanPath.startsWith('storage/') ? cleanPath : `storage/${cleanPath}`;
            imageUrl = `${baseUrl}/${finalPath}`;
          }
        }
        
        return (
          <Flex justifyContent='center' alignItems='center'>
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt='Sponsor Logo'
                w='50px'
                h='50px'
                borderRadius='md'
                objectFit='cover'
                fallbackSrc='https://via.placeholder.com/50?text=No+Image'
              />
            ) : (
              <Text color='gray.400' fontSize='xs'>
                No Image
              </Text>
            )}
          </Flex>
        )
      },
    }),

    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => (
        <Text
          justifyContent='center'
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Actions
        </Text>
      ),
      cell: ({ row }) => (
        <Flex justifyContent='center' alignItems='center'>
          <ButtonGroup size='sm' isAttached>
            <Button
              colorScheme='brand'
              onClick={() => handleEditOpen(row.original)}
            >
              <EditIcon />
            </Button>
            <Button
              colorScheme='red'
              onClick={() => handleSingleDelete(row.original.id)}
            >
              <AiOutlineDelete />
            </Button>
          </ButtonGroup>
        </Flex>
      ),
      enableSorting: false,
    }),
  ]

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) {
    return <SkeletonTable />
  }

  if (isError || !data) {
    return (
      <VStack spacing={4} align='center' mt='20'>
        <Alert status='error' variant='subtle'>
          <AlertIcon />
          <Box>
            <AlertTitle>Error Occurred!</AlertTitle>
            <AlertDescription>
              Unable to load sponsor data. Please try again later.
            </AlertDescription>
          </Box>
        </Alert>
        <Button onClick={refetch} colorScheme='red' isLoading={isLoading}>
          Retry
        </Button>
      </VStack>
    )
  }

  return (
    <Card
      flexDirection='column'
      w='100%'
      px='0px'
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px='25px' mb='8px' justifyContent='space-between' align='center'>
        <Text
          color={textColor}
          fontSize='22px'
          fontWeight='700'
          lineHeight='100%'
        >
          Sponsors
        </Text>
        <Button
          leftIcon={<AiOutlinePlus />}
          colorScheme='brand'
          size='sm'
          borderRadius='12px'
          onClick={onOpenCreate}
        >
          New Sponsor
        </Button>
        <DeleteSponsorModal
          isOpen={isOpen}
          onClose={onClose}
          onDelete={onDelete}
          isLoading={isPending}
          isDisabled={isPending}
        />
        <CreateSponsorModal isOpen={isCreateOpen} onClose={onCloseCreate} />
        <EditSponsorModal
          isOpen={isEditOpen}
          onClose={handleEditClose}
          sponsor={selectedSponsor}
        />
      </Flex>
      {rows.length === 0 ? (
        <VStack my='20' spacing='4' align='center'>
          <Text fontSize='lg' color='gray.500'>
            No sponsors found.
          </Text>
          <Button colorScheme='blue' onClick={onOpenCreate}>
            Add a Sponsor
          </Button>
        </VStack>
      ) : (
        <>
          <Box
            maxH='600px'
            overflowY='auto'
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#999 #ddd' }}
          >
            <Table color='gray.500' mb='24px' mt='12px'>
              <Thead position='sticky' zIndex='1' top='0' bg={TheadBackground}>
                {table.getHeaderGroups().map(headerGroup => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        borderColor={borderColor}
                        textAlign='center'
                      >
                        <Flex
                          justifyContent='center'
                          align='center'
                          fontSize={{ sm: '10px', lg: '12px' }}
                          color='gray.400'
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map(row => (
                  <Tr
                    key={row.id}
                    _hover={{ bg: hoverBackgroundColor }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <Td key={cell.id} textAlign='center'>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <CustomPagination
            total={data?.total || 0}
            current={pagination.current}
            pageSize={pagination.pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </>
      )}
    </Card>
  )
}
